import React, { useState, useRef, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Eye, EyeOff, X } from 'lucide-react';
import { BASE_URL } from '../utils/api';
import { GoogleLogin } from '@react-oauth/google';
import logo512 from '../assets/logo512.png';

// Fix #1: API key now comes from env — never hardcoded
const getApiKey = () => process.env.REACT_APP_API_KEY || '';


const Login = ({ onLogin }) => {
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [googleLoading, setGoogleLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });

  // Fix #3: Rate limiting — track last submission time to prevent brute-force
  const lastSubmitTime = useRef(0);
  const SUBMIT_COOLDOWN_MS = 2000;

  // Handles the id_token credential returned by the GoogleLogin component
  const handleGoogleCredential = useCallback(async (credentialResponse) => {
    setGoogleLoading(true);
    setError('');
    try {
      const res = await fetch(`${BASE_URL}/auth/google-signin`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Api-Key': getApiKey(), // Fix #1
        },
        body: JSON.stringify({ idToken: credentialResponse.credential }),
      });

      const contentType = res.headers.get('content-type');
      let data = {};
      if (contentType && contentType.includes('application/json')) {
        data = await res.json();
      }

      if (res.ok) {
        onLogin(data);
        navigate('/');
      } else {
        setError(data.message || data.error || 'Google sign-in failed. Please try again.');
      }
    } catch (err) {
      setError('Connection failed. Please check your network.');
    } finally {
      setGoogleLoading(false);
    }
  }, [onLogin, navigate]);

  const handleGoogleError = useCallback(() => {
    setError('Google sign-in was cancelled or failed.');
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Fix #3: Enforce minimum time between submissions
    const now = Date.now();
    if (now - lastSubmitTime.current < SUBMIT_COOLDOWN_MS) {
      setError('Please wait a moment before trying again.');
      return;
    }
    lastSubmitTime.current = now;

    setIsSubmitting(true);
    try {
      const response = await fetch(`${BASE_URL}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-Api-Key': getApiKey(), // Fix #1
        },
        body: JSON.stringify(formData)
      });

      const contentType = response.headers.get("content-type");
      let data = {};
      if (contentType && contentType.includes("application/json")) {
        data = await response.json();
      }

      if (response.ok) {
        onLogin(data);
        navigate('/');
      } else if (response.status === 429) {
        setError('Too many login attempts. Please wait before trying again.');
      } else {
        setError(data.message || data.error || `Server Error: ${response.status}`);
      }
    } catch (err) {
      setError("Connection failed. Check your network or restart the server.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="flex items-center justify-center min-h-screen bg-transparent p-4 font-sans relative animate-in zoom-in-95 duration-500">
      <div className="w-full max-w-md glass-panel rounded-[2.5rem] p-10 flex flex-col items-center relative transform hover:scale-[1.01] transition-transform duration-500 shadow-2xl shadow-game-neon/20">

        {/* --- Close / Home Button --- */}
        <button
          onClick={() => navigate('/')}
          className="absolute top-6 right-6 p-2 text-game-muted hover:text-white hover:bg-white/10 rounded-full transition-all"
          title="Back to Home"
        >
          <X size={24} />
        </button>

        {/* Brand Logo Header */}
        <div className="w-24 h-24 mb-6 flex items-center justify-center relative group">
          <div className="absolute inset-0 bg-gradient-to-tr from-[#FF5E0E] to-[#FFB800] rounded-full blur-xl opacity-35 group-hover:opacity-60 transition-opacity" />
          <img 
            src={logo512} 
            alt="KarnoGames" 
            className="w-full h-full object-contain relative z-10 drop-shadow-[0_8px_20px_rgba(255,94,14,0.45)] transform group-hover:scale-105 transition-transform" 
          />
        </div>

        {/* Title and Subtitle */}
        <h2 className="text-3xl font-heading font-black text-white mb-2 text-center drop-shadow-[0_0_10px_rgba(255,255,255,0.2)]">Welcome Back</h2>
        <p className="text-game-muted mb-8 text-center text-sm font-medium">Login to continue your gaming journey</p>

        {error && (
          <div className="w-full mb-4 p-3 bg-red-500/10 text-red-400 text-xs rounded-xl border border-red-500/30 font-bold uppercase tracking-tight shadow-[0_0_10px_rgba(239,68,68,0.2)]">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="w-full space-y-6">
          <div className="space-y-2">
            <label className="text-sm font-bold text-game-muted block ml-1 uppercase tracking-wider">Username or Email</label>
            <input
              type="email"
              placeholder="Enter your email"
              required
              value={formData.email}
              onChange={(e) => setFormData({...formData, email: e.target.value})}
              className="w-full bg-game-dark/50 border border-game-border rounded-2xl p-4 text-sm text-white outline-none focus:border-game-neon focus:shadow-[0_0_15px_rgba(255,184,0,0.2)] transition-all placeholder:text-gray-600"
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-bold text-game-muted block ml-1 uppercase tracking-wider">Password</label>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                placeholder="Enter password"
                required
                value={formData.password}
                onChange={(e) => setFormData({...formData, password: e.target.value})}
                className="w-full bg-game-dark/50 border border-game-border rounded-2xl p-4 text-sm text-white outline-none focus:border-game-neon focus:shadow-[0_0_15px_rgba(255,184,0,0.2)] transition-all placeholder:text-gray-600"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
              >
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
          </div>

          {/* Fix #3: Button disabled during submission cooldown */}
          <button
            type="submit"
            disabled={isSubmitting}
            className="game-btn w-full py-4 rounded-2xl mt-2 text-lg shadow-[0_0_20px_rgba(255,94,14,0.4)] disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {isSubmitting ? 'Logging in...' : 'Login'}
          </button>
        </form>

        <div className="w-full flex items-center my-6 opacity-50">
          <div className="flex-1 h-[1px] bg-game-border"></div>
          <span className="px-4 text-xs text-game-muted font-bold uppercase tracking-widest">or</span>
          <div className="flex-1 h-[1px] bg-game-border"></div>
        </div>

        {/* Google Sign-In */}
        <div className="w-full flex flex-col items-center gap-3 mb-6">
          {googleLoading ? (
            <div className="w-full flex items-center justify-center gap-3 py-3.5 rounded-full border border-white/10 bg-white/5">
              <div className="w-5 h-5 border-2 border-white/20 border-t-white rounded-full animate-spin"></div>
              <span className="text-sm text-game-muted font-semibold">Signing in with Google...</span>
            </div>
          ) : (
            <div className="w-full flex justify-center py-0.5">
              <GoogleLogin
                onSuccess={handleGoogleCredential}
                onError={handleGoogleError}
                theme="filled_black"
                shape="pill"
                size="large"
                text="continue_with"
                width="380"
                logo_alignment="left"
              />
            </div>
          )}
        </div>

        <p className="text-sm text-game-muted font-medium mb-8">
          Don't have an account? <Link to="/signup" className="text-game-neon font-bold hover:underline transition-all">Create Account</Link>
        </p>

        {/* Fix #9: Demo mode banner only shown during development */}
        {process.env.NODE_ENV === 'development' && (
          <div className="w-full bg-game-accent/10 border border-game-accent/30 rounded-2xl p-5 shadow-[0_0_15px_rgba(255,94,14,0.1)]">
            <p className="text-[12px] text-game-accent text-center leading-relaxed font-bold">
              <span className="font-black uppercase mr-1 text-game-neon tracking-widest">Dev Mode:</span>
              Create a new account or use any existing username to login
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Login;